import { MultiSelectField, MultiSelectFieldProps } from "../../inputFields/MultiSelectField";
import { Primitive } from "src/types/primitive";
import { SimpleSelectFieldOption } from "src/components/common/inputFields/types";
import { useController, UseControllerProps } from "react-hook-form";

export interface FormMultiSelectFieldProps<TFieldValues extends object, Key extends Primitive>
	extends Omit<
		MultiSelectFieldProps<SimpleSelectFieldOption<Key>, Key>,
		"onChange" | "value" | "getOptionKey" | "getOptionLabel"
	> {
	control: UseControllerProps<TFieldValues>["control"];
	name: UseControllerProps<TFieldValues>["name"];
	rules?: UseControllerProps<TFieldValues>["rules"];
	encodeValue?: (v: Key[]) => string;
	decodeValue?: (v: string) => Key[];
}

export const FormMultiSelectField = <TFieldValues extends object, Key extends Primitive>({
	control,
	name,
	rules,
	options,
	encodeValue,
	decodeValue,
	...other
}: FormMultiSelectFieldProps<TFieldValues, Key>) => {
	const { field, fieldState } = useController({
		name,
		control,
		rules,
	});

	const transformedField = {
		...field,
		value: decodeValue ? decodeValue(field.value) : field.value,
		onChange: (v: Key[]) => {
			field.onChange(encodeValue ? encodeValue(v) : v);
		},
		ref: undefined,
	};

	return (
		<MultiSelectField
			options={options}
			getOptionKey={<Key extends Primitive>(
				o: SimpleSelectFieldOption<Key>,
			): Key => {
				return o.key;
			}}
			getOptionLabel={<Key extends Primitive>(
				o: SimpleSelectFieldOption<Key>,
			): string => {
				return o.label;
			}}
			error={fieldState.error?.message}
			{...transformedField}
			{...other}
		/>
	);
};
